import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

const COLUMN_ORDER = [
    'actions',
    'created_at',
    'status',
    'intended_recipient_email',
    'intended_recipient_name',
    'subject',
    'contract_id',
    'email_type',
    'subscriber_email',
    'db_id'
];

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
            <div style={{
                backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                width: '90%', maxWidth: '900px', maxHeight: '90%',
                display: 'flex', flexDirection: 'column', position: 'relative',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>{title}</h3>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: '1.5rem',
                        cursor: 'pointer', color: '#6b7280'
                    }}>×</button>
                </div>
                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

function DeveloperPendingEmails() {
    const { user, isAuthenticated, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();

    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Modal State
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const handleViewEmail = (email) => {
        setSelectedEmail(email);
        setIsModalOpen(true);
    };

    const fetchPendingEmails = async () => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`${BACKEND_URL}/pending-emails`);
            setEmails(response.data || []);
        } catch (e) {
            setError(e.message || 'Failed to load pending emails');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchPendingEmails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser]);

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }
    if (!isKeyFloUser) return null;

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Pending Emails (Ask First)</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back</button>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={fetchPendingEmails}>Refresh</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            {COLUMN_ORDER.map((col) => (
                                <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                                    {col.replace(/_/g, ' ').toUpperCase()}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {emails.map((email, idx) => (
                            <tr key={email.db_id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                {COLUMN_ORDER.map((col) => {
                                    if (col === 'actions') {
                                        return (
                                            <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                                <button
                                                    onClick={() => handleViewEmail(email)}
                                                    style={{
                                                        padding: '4px 8px', fontSize: '0.75rem',
                                                        background: '#3b82f6', color: '#fff',
                                                        border: 'none', borderRadius: '4px', cursor: 'pointer'
                                                    }}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        );
                                    }
                                    if (col === 'status') {
                                        const status = email[col] || '';
                                        const color = status === 'pending' ? '#D97706' : status === 'approved' ? '#059669' : '#6B7280';
                                        const bg = status === 'pending' ? '#FEF3C7' : status === 'approved' ? '#D1FAE5' : '#F3F4F6';
                                        return (
                                            <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{
                                                    display: 'inline-block', padding: '2px 8px',
                                                    borderRadius: '12px', fontSize: '0.8rem',
                                                    fontWeight: 600, color, backgroundColor: bg
                                                }}>
                                                    {status.toUpperCase()}
                                                </span>
                                            </td>
                                        );
                                    }
                                    let val = email[col];
                                    if (col === 'created_at' || col === 'decided_at') val = formatDateTime(val);
                                    return (
                                        <td key={col} style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem' }}>
                                            {String(val || '')}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {emails.length === 0 && !loading && (
                            <tr>
                                <td colSpan={COLUMN_ORDER.length} style={{ padding: '12px', textAlign: 'center' }}>No pending emails found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`Email Preview: ${selectedEmail?.subject || 'No Subject'}`}
            >
                {selectedEmail?.body_html ? (
                    <iframe
                        title="Email Content"
                        srcDoc={selectedEmail.body_html}
                        style={{ width: '100%', height: '600px', border: 'none' }}
                    />
                ) : (
                    <div style={{ padding: '16px', color: '#6b7280', fontStyle: 'italic' }}>No content available</div>
                )}
            </Modal>
        </div>
    );
}

export default DeveloperPendingEmails;
